import { EVolumeType } from 'features/index/model/EVolumeType';
import { QueueType } from '../models/enums/QueueType';

export type ColumnOption = {
    label: string;
    value: string;
};

export type BulkUploadColumnDef = {
    templateName: string;
    paramKey: string;
    label: string;
    options?: ColumnOption[];
    isBool?: true;
};

const PATH_COLUMN: BulkUploadColumnDef = {
    templateName: 'network_full_file_path',
    paramKey: 'Path',
    label: 'File Path',
};

const DOMAIN_OPTIONS: ColumnOption[] = [
    { label: 'Depth', value: '1' },
    { label: 'Time', value: '0' },
];

const SCALAR_OPTIONS: ColumnOption[] = [
    { label: '1', value: '1' },
    { label: '10', value: '0.1' },
    { label: '100', value: '0.01' },
    { label: '1000', value: '0.001' },
];

const PERMISSION_OPTIONS: ColumnOption[] = [
    { label: 'Private', value: '0' },
    { label: 'Public', value: '1' },
    { label: 'In Company', value: '2' },
];

const COMPOSITE_PROFILE_TYPE_OPTIONS: ColumnOption[] = [
    { label: 'Composite Profile', value: '0' },
    { label: 'PAG', value: '1' },
    { label: 'Perfil de Fácies', value: '2' },
];

const SEISMIC_COLUMNS: BulkUploadColumnDef[] = [
    PATH_COLUMN,
    { templateName: 'domain', paramKey: 'Domain', label: 'Domain', options: DOMAIN_OPTIONS },
    { templateName: 'srid', paramKey: 'Srid', label: 'SRID' },
    { templateName: 'coordinate_x', paramKey: 'CoordinateX', label: 'Coordinate X' },
    { templateName: 'coordinate_y', paramKey: 'CoordinateY', label: 'Coordinate Y' },
    { templateName: 'inline', paramKey: 'Inline', label: 'Inline' },
    { templateName: 'x_line', paramKey: 'XLine', label: 'XLine' },
    { templateName: 'trace', paramKey: 'Trace', label: 'Trace' },
    { templateName: 'cdp', paramKey: 'Cdp', label: 'CDP' },
    { templateName: 'shot_point', paramKey: 'ShotPoint', label: 'Shot Point' },
    { templateName: 'byte_scalar', paramKey: 'ByteScalar', label: 'Byte Scalar' },
    { templateName: 'scalar_x', paramKey: 'ScalarX', label: 'Scalar X', options: SCALAR_OPTIONS },
    { templateName: 'scalar_y', paramKey: 'ScalarY', label: 'Scalar Y', options: SCALAR_OPTIONS },
    { templateName: 'auto_publish', paramKey: 'AutoPublish', label: 'Auto Publish', isBool: true },
];

const SHAPEFILE_COLUMNS: BulkUploadColumnDef[] = [
    PATH_COLUMN,
    { templateName: 'srid', paramKey: 'Srid', label: 'SRID' },
    { templateName: 'data_source_id', paramKey: 'DataSourceId', label: 'Data Source ID' },
    { templateName: 'group_id', paramKey: 'GroupId', label: 'Group ID' },
    { templateName: 'permission', paramKey: 'Permission', label: 'Permission', options: PERMISSION_OPTIONS },
];

const TIFF_COLUMNS: BulkUploadColumnDef[] = [PATH_COLUMN];
const GRID_COLUMNS: BulkUploadColumnDef[] = [PATH_COLUMN];

const COMPOSITE_PROFILE_COLUMNS: BulkUploadColumnDef[] = [
    PATH_COLUMN,
    { templateName: 'type', paramKey: 'Type', label: 'Type', options: COMPOSITE_PROFILE_TYPE_OPTIONS },
    { templateName: 'density', paramKey: 'Density', label: 'Density' },
    { templateName: 'auto_publish', paramKey: 'AutoPublish', label: 'Auto Publish', isBool: true },
];

export const getColumnDefinitions = (queueType: QueueType): BulkUploadColumnDef[] => {
    switch (queueType) {
        case QueueType.UploadSeismic2D:
        case QueueType.UploadSeismic3D:
            return SEISMIC_COLUMNS;
        case QueueType.UploadShapefile:
            return SHAPEFILE_COLUMNS;
        case QueueType.UploadTiff:
            return TIFF_COLUMNS;
        case QueueType.UploadGrid:
            return GRID_COLUMNS;
        case QueueType.CompositeProfile:
            return COMPOSITE_PROFILE_COLUMNS;
        default:
            return [PATH_COLUMN];
    }
};

export const getValidExtensions = (queueType: QueueType): string[] => {
    switch (queueType) {
        case QueueType.UploadSeismic2D:
        case QueueType.UploadSeismic3D:
            return ['.sgy', '.segy'];
        case QueueType.UploadShapefile:
            return ['.shp', '.shx', '.dbf', '.prj', '.cpg', '.sbn', '.sbx', '.cst'];
        case QueueType.CompositeProfile:
            return ['.pdf'];
        default:
            return [];
    }
};

export const generateTemplateCsv = (queueType: QueueType): string => {
    const columns = getColumnDefinitions(queueType);
    return columns.map(column => column.templateName).join(',') + '\n';
};

const normalize = (value: string): string => value.toLowerCase().replace(/[_\s-]/g, '');

const getBigrams = (value: string): string[] => {
    const bigrams: string[] = [];
    for (let i = 0; i < value.length - 1; i++) {
        bigrams.push(value.slice(i, i + 2));
    }
    return bigrams;
};

const getBigramSimilarity = (first: string, second: string): number => {
    if (first === second) {
        return 1;
    }
    if (first.length < 2 || second.length < 2) {
        return first === second ? 1 : 0;
    }
    const firstBigrams = getBigrams(first);
    const secondBigramSet = new Set(getBigrams(second));
    const intersectionCount = firstBigrams.filter(bigram => secondBigramSet.has(bigram)).length;
    return (2 * intersectionCount) / (firstBigrams.length + secondBigramSet.size);
};

const getColumnScore = (csvColumn: string, column: BulkUploadColumnDef): number => {
    const normalizedCsv = normalize(csvColumn);
    const scores = [
        getBigramSimilarity(normalizedCsv, normalize(column.templateName)),
        getBigramSimilarity(normalizedCsv, normalize(column.paramKey)),
        getBigramSimilarity(normalizedCsv, normalize(column.label)),
    ];
    return Math.max(...scores);
};

const MIN_SIMILARITY_THRESHOLD = 0.25;

export const findBestColumnMatch = (csvColumn: string, columns: BulkUploadColumnDef[]): string | null => {
    let bestColumn: BulkUploadColumnDef | null = null;
    let bestScore = 0;
    for (const column of columns) {
        const score = getColumnScore(csvColumn, column);
        if (score > bestScore) {
            bestScore = score;
            bestColumn = column;
        }
    }
    if (bestColumn !== null && bestScore >= MIN_SIMILARITY_THRESHOLD) {
        return bestColumn.paramKey;
    }
    return null;
};

export const findBestCsvColumnForParam = (
    paramKey: string,
    csvHeaders: string[],
    columns: BulkUploadColumnDef[]
): string | null => {
    const columnDef = columns.find(column => column.paramKey === paramKey);
    if (!columnDef) {
        return null;
    }
    let bestHeader: string | null = null;
    let bestScore = 0;
    for (const csvHeader of csvHeaders) {
        const score = getColumnScore(csvHeader, columnDef);
        if (score > bestScore) {
            bestScore = score;
            bestHeader = csvHeader;
        }
    }
    if (bestHeader !== null && bestScore >= MIN_SIMILARITY_THRESHOLD) {
        return bestHeader;
    }
    return null;
};

export const getExtraSubmitParams = (queueType: QueueType): Record<string, string> => {
    switch (queueType) {
        case QueueType.UploadSeismic2D:
            return { VolumeType: String(EVolumeType.Seismic2D), ParallelProcesses: '20' };
        case QueueType.UploadSeismic3D:
            return { VolumeType: String(EVolumeType.Seismic3D), ParallelProcesses: '20' };
        default:
            return {};
    }
};

export const resolveOptionValue = (rawValue: string, columnDef: BulkUploadColumnDef): string | null => {
    if (columnDef.options) {
        const lower = rawValue.toLowerCase();
        const match = columnDef.options.find(
            option => option.label.toLowerCase() === lower || option.value.toLowerCase() === lower
        );
        return match ? match.value : null;
    }
    if (columnDef.isBool) {
        const lower = rawValue.toLowerCase();
        if (lower === 'true' || lower === 'false') {
            return lower;
        }
        return null;
    }
    return null;
};

export const resolveDisplayValue = (rawValue: string, columnDef: BulkUploadColumnDef): string => {
    if (!rawValue) {
        return '';
    }
    if (columnDef.options) {
        const lower = rawValue.toLowerCase();
        const match = columnDef.options.find(
            option => option.label.toLowerCase() === lower || option.value.toLowerCase() === lower
        );
        if (match) {
            return `${rawValue} (${match.value})`;
        }
        return rawValue;
    }
    if (columnDef.isBool) {
        const lower = rawValue.toLowerCase();
        if (lower === 'true' || lower === 'false') {
            return lower;
        }
        return rawValue;
    }
    return rawValue;
};
